import React from 'react';
import {
  Disc3,
  Square,
  Maximize2,
  Minimize2,
  Sparkles,
  Settings,
  Layers,
  Monitor,
  MonitorOff,
  Users,
  Hand,
  Smile,
  ArrowUp,
  ScrollText,
} from 'lucide-react';

interface MoreOptionsMenuProps {
  isRecording: boolean;
  isSavingRecording: boolean;
  isFullscreen: boolean;
  transparentMode: boolean;
  isScreenSharing?: boolean;
  isHandRaised?: boolean;
  showReactions?: boolean;
  showParticipants?: boolean;
  mobileExtras?: boolean;
  /** Host: tutanak / otomatik döküm */
  canToggleMinutes?: boolean;
  minutesEnabled?: boolean;
  onToggleMinutes?: () => void;
  onToggleRecording: () => void;
  onToggleFullscreen: () => void;
  onOpenBackgroundEffects: () => void;
  onOpenSettings: () => void;
  onToggleTransparent: () => void;
  onToggleScreenShare?: () => void;
  onToggleParticipants?: () => void;
  onToggleReactions?: () => void;
  onToggleHandRaise?: () => void;
  onClose: () => void;
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}

function MenuItem({ icon, label, onClick, disabled, danger }: MenuItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition disabled:opacity-50 ${
        danger ? 'hover:bg-red-600/15' : 'hover:bg-white/10'
      }`}
    >
      <span className="w-5 h-5 flex items-center justify-center text-gray-300 flex-shrink-0">{icon}</span>
      <span className="text-white text-sm">{label}</span>
    </button>
  );
}

const MoreOptionsMenu: React.FC<MoreOptionsMenuProps> = ({
  isRecording,
  isSavingRecording,
  isFullscreen,
  transparentMode,
  isScreenSharing = false,
  isHandRaised = false,
  showReactions = false,
  showParticipants = false,
  mobileExtras = false,
  canToggleMinutes = false,
  minutesEnabled = false,
  onToggleMinutes,
  onToggleRecording,
  onToggleFullscreen,
  onOpenBackgroundEffects,
  onOpenSettings,
  onToggleTransparent,
  onToggleScreenShare,
  onToggleParticipants,
  onToggleReactions,
  onToggleHandRaise,
  onClose,
}) => {
  const run = (fn: () => void) => {
    fn();
    onClose();
  };

  return (
    <div className="absolute bottom-full right-0 mb-3 w-64 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_-12px_40px_rgba(0,0,0,0.55)] overflow-hidden z-[70] py-1.5 ring-1 ring-purple-500/10">
      {mobileExtras && (
        <>
          <MenuItem
            icon={
              isScreenSharing ? (
                <MonitorOff className="w-4 h-4 text-purple-400" />
              ) : (
                <div className="relative">
                  <Monitor className="w-4 h-4 text-purple-400" />
                  <ArrowUp className="w-2 h-2 absolute -top-1 -right-1 text-purple-300" />
                </div>
              )
            }
            label={isScreenSharing ? 'Ekran paylaşımını durdur' : 'Ekran paylaş'}
            onClick={() => onToggleScreenShare && run(onToggleScreenShare)}
          />
          <MenuItem
            icon={<Users className="w-4 h-4" />}
            label={showParticipants ? 'Katılımcıları gizle' : 'Katılımcıları göster'}
            onClick={() => onToggleParticipants && run(onToggleParticipants)}
          />
          <MenuItem
            icon={<Hand className={`w-4 h-4 ${isHandRaised ? 'text-amber-400' : ''}`} />}
            label={isHandRaised ? 'Söz istemeyi bırak' : 'Söz iste'}
            onClick={() => onToggleHandRaise && run(onToggleHandRaise)}
          />
          <MenuItem
            icon={<Smile className={`w-4 h-4 ${showReactions ? 'text-purple-400' : ''}`} />}
            label={showReactions ? 'Emoji menüsünü gizle' : 'Emoji gönder'}
            onClick={() => onToggleReactions && run(onToggleReactions)}
          />
          <div className="border-t border-white/10 my-1" />
        </>
      )}
      <MenuItem
        icon={isRecording ? <Square className="w-4 h-4 text-red-400" /> : <Disc3 className="w-4 h-4 text-red-400" />}
        label={
          isSavingRecording
            ? 'Kayıt kaydediliyor...'
            : isRecording
              ? 'Kaydı durdur'
              : 'Toplantıyı kaydet'
        }
        onClick={() => {
          if (!isSavingRecording) onToggleRecording();
        }}
        disabled={isSavingRecording}
        danger={isRecording}
      />
      {canToggleMinutes && onToggleMinutes && (
        <MenuItem
          icon={<ScrollText className={`w-4 h-4 ${minutesEnabled ? 'text-amber-400' : ''}`} />}
          label={minutesEnabled ? 'Tutanağı kapat' : 'Tutanak / otomatik döküm'}
          onClick={() => run(onToggleMinutes)}
        />
      )}
      <div className="border-t border-white/10 my-1" />
      <MenuItem
        icon={isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        label={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
        onClick={() => run(onToggleFullscreen)}
      />
      <MenuItem
        icon={<Sparkles className="w-4 h-4" />}
        label="Arka planlar ve efektler"
        onClick={() => run(onOpenBackgroundEffects)}
      />
      <MenuItem
        icon={<Layers className="w-4 h-4" />}
        label={transparentMode ? 'Şeffaf modu kapat' : 'Şeffaf mod'}
        onClick={() => run(onToggleTransparent)}
      />
      <div className="border-t border-white/10 my-1" />
      <MenuItem
        icon={<Settings className="w-4 h-4" />}
        label="Ayarlar"
        onClick={() => run(onOpenSettings)}
      />
    </div>
  );
};

export default MoreOptionsMenu;
